import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import apiClient from '../config/axios';
import ModalConfirm from './ModalConfirm';
import ModalResult from './ModalResult';
import dayjs from 'dayjs';

const getTodayDateString = () => dayjs().format('YYYY-MM-DD');
const availableWallNumbers = Array.from({ length: 15 }, (_, idx) => idx + 1);
const availableWallColors = ['giallo', 'nero', 'verde', 'rossa', 'blu', 'bianco', 'arancione', 'viola'];
const wallColorMap = {
  giallo: '#FBC02D',
  gialla: '#FBC02D',
  nero: '#212121',
  nera: '#212121',
  verde: '#388E3C',
  rossa: '#D32F2F',
  blu: '#1976D2',
  bianco: '#FAFAFA',
  bianca: '#FAFAFA',
  arancione: '#F57C00',
  viola: '#8E24AA',
};

const toDateInputValue = (value) => {
  if (!value) return getTodayDateString();
  const d = dayjs(value);
  if (!d.isValid()) return getTodayDateString();
  return d.format('YYYY-MM-DD');
};

const ModalWall = ({ open, onClose, onSuccess, editData = null, isEdit = false }) => {
  const [formData, setFormData] = useState({
    wallNumber: '',
    wallColor: '',
    officialGrade: '',
    notes: '',
    createdAt: getTodayDateString(),
  });
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [resultData, setResultData] = useState({ message: '', severity: 'info' });

  // Popola il form quando è in modalità edit
  useEffect(() => {
    if (isEdit && editData) {
      setFormData({
        wallNumber: editData.wallNumber || '',
        wallColor: editData.wallColor || '',
        officialGrade: editData.officialGrade || '',
        notes: editData.notes || '',
        createdAt: toDateInputValue(editData.createdAt),
      });
    } else {
      setFormData({
        wallNumber: '',
        wallColor: '',
        officialGrade: '',
        notes: '',
        createdAt: getTodayDateString(),
      });
    }
  }, [isEdit, editData, open]);

  // Gradi disponibili da 4a a 9a
  const availableGrades = [
    '4a', '4a+', '4b', '4b+', '4c', '4c+',
    '5a', '5a+', '5b', '5b+', '5c', '5c+',
    '6a', '6a+', '6b', '6b+', '6c', '6c+',
    '7a', '7a+', '7b', '7b+', '7c', '7c+',
    '8a', '8a+', '8b', '8b+', '8c', '8c+',
    '9a'
  ];

  const handleChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const wallNumber = Number(formData.wallNumber);
    if (!availableWallNumbers.includes(wallNumber) || !availableWallColors.includes(formData.wallColor)) {
      return;
    }

    setShowConfirm(true);
  };

  const handleConfirmSubmit = async () => {
    try {
      setLoading(true);
      setShowConfirm(false);

      const payload = {
        ...formData,
        wallNumber: Number(formData.wallNumber),
        wallColor: formData.wallColor,
        createdAt: formData.createdAt ? dayjs(formData.createdAt, 'YYYY-MM-DD').toISOString() : undefined,
      };
      
      if (isEdit) {
        // Update existing wall
        await apiClient.put(`/api/walls/${editData._id}`, payload);
        setResultData({
          message: 'Via aggiornata con successo!',
          severity: 'success'
        });
      } else {
        // Create new wall
        await apiClient.post('/api/walls', payload);
        setResultData({
          message: 'Via creata con successo!',
          severity: 'success'
        });
      }
      
      setShowResult(true);
      
      // Reset form
      setFormData({
        wallNumber: '',
        wallColor: '',
        officialGrade: '',
        notes: '',
        createdAt: getTodayDateString(),
      });
      
    } catch (err) {
      console.error('Errore nell\'operazione della via:', err);
      
      let errorMessage = isEdit ? 'Errore nell\'aggiornamento della via' : 'Errore nella creazione della via';
      if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      }
      
      setResultData({
        message: errorMessage,
        severity: 'error'
      });
      setShowResult(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelConfirm = () => {
    setShowConfirm(false);
  };

  const handleResultClose = () => {
    setShowResult(false);
    // Chiudi sempre il modal principale dopo aver mostrato il risultato
    onClose();
    onSuccess?.();
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        wallNumber: '',
        wallColor: '',
        officialGrade: '',
        notes: '',
        createdAt: getTodayDateString(),
      });
      setShowConfirm(false);
      setShowResult(false);
      setResultData({ message: '', severity: 'info' });
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm" 
      fullWidth
      disableEscapeKeyDown
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <AddIcon color="primary" />
          {isEdit ? 'Modifica Via' : 'Aggiungi Via'}
        </Box>
      </DialogTitle>
      
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {isEdit && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>Importante:</strong> Se stai modificando questa via perché è una nuova via, allora eliminala e creane una nuova.
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Data di Creazione"
              type="date"
              value={formData.createdAt}
              onChange={handleChange('createdAt')}
              slotProps={{ inputLabel: { shrink: true } }}
            />

            <FormControl fullWidth required>
              <InputLabel>Parete</InputLabel>
              <Select value={formData.wallNumber} onChange={handleChange('wallNumber')} label="Numero Via">
                {availableWallNumbers.map((wallNumber) => (
                  <MenuItem key={wallNumber} value={wallNumber}>
                    {wallNumber}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth required>
              <InputLabel>Colore</InputLabel>
              <Select
                value={formData.wallColor}
                onChange={handleChange('wallColor')}
                label="Colore"
                renderValue={(selectedColor) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        bgcolor: wallColorMap[selectedColor] || '#9E9E9E',
                        border: selectedColor === 'bianco' ? '1px solid #BDBDBD' : 'none',
                      }}
                    />
                    <Box component="span">{selectedColor}</Box>
                  </Box>
                )}
              >
                {availableWallColors.map((color) => (
                  <MenuItem key={color} value={color}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: '50%',
                          bgcolor: wallColorMap[color] || '#9E9E9E',
                          border: color === 'bianco' ? '1px solid #BDBDBD' : 'none',
                        }}
                      />
                      <Box component="span">{color}</Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Grado Ufficiale</InputLabel>
              <Select
                value={formData.officialGrade}
                onChange={handleChange('officialGrade')}
                label="Grado Ufficiale"
              >
                <MenuItem value="">
                  <em>Nessun grado ufficiale</em>
                </MenuItem>
                {availableGrades.map((grade) => (
                  <MenuItem key={grade} value={grade}>
                    {grade}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Note (opzionale)"
              multiline
              rows={3}
              value={formData.notes}
              onChange={handleChange('notes')}
              placeholder="Aggiungi note sulla via..."
              inputProps={{ maxLength: 500 }}
              helperText={`${formData.notes.length}/500 caratteri`}
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button 
            onClick={handleClose} 
            disabled={loading}
            color="inherit"
          >
            Annulla
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={loading || !availableWallNumbers.includes(Number(formData.wallNumber)) || !availableWallColors.includes(formData.wallColor)}
            startIcon={loading ? <CircularProgress size={20} /> : <AddIcon />}
          >
            {loading ? (isEdit ? 'Aggiornando...' : 'Creando...') : (isEdit ? 'Aggiorna Via' : 'Crea Via')}
          </Button>
        </DialogActions>
      </form>

      <ModalConfirm
        open={showConfirm}
        title={isEdit ? "Conferma Modifica Via" : "Conferma Creazione Via"}
        message={`Sei sicuro di voler ${isEdit ? 'aggiornare' : 'creare'} la via Parte ${formData.wallNumber} - ${formData.wallColor}${formData.officialGrade ? ` con grado ${formData.officialGrade}` : ''}?`}
        onConfirm={handleConfirmSubmit}
        onCancel={handleCancelConfirm}
      />

      <ModalResult
        open={showResult}
        onClose={handleResultClose}
        message={resultData.message}
        severity={resultData.severity}
        title={resultData.severity === 'success' ? 'Successo' : 'Errore'}
      />
    </Dialog>
  );
};

export default ModalWall;
